import { COMMA, ENTER, SPACE } from '@angular/cdk/keycodes';
import { DOCUMENT } from '@angular/common';
import {
  Component,
  ElementRef,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatChipInputEvent, MatChipList } from '@angular/material/chips';
import { Subscription } from 'rxjs';
import { AlcmeonContextService } from 'src/app/core/alcmeon-context/alcmeon-context.service';
import { AbstractComponentWithScrollContainer } from 'src/app/shared/components/abstract-component-with-scroll-container';
import { ReplyToEmailModeEnum, SendByEmailSettings } from 'src/app/shared/models/send-by-mail';
import { BasicService } from 'src/app/shared/services/basic.service';
import { SendConversationByEmailService } from 'src/app/shared/services/send-by-email.service';
import { environment } from 'src/environments/environment';

import { ActionType, Recipient, RecipientError, SendByEmailPayload } from '../models';

/**
 * Send conversation by email modal
 * Not available in mobile
 */
@Component({
  selector: 'app-send-by-email-popover',
  templateUrl: './send-by-email-popover.component.html',
  styleUrls: ['./send-by-email-popover.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class SendByEmailPopoverComponent extends AbstractComponentWithScrollContainer implements OnInit, OnDestroy {
  @Input() processedTask: any;
  @Input() reOpenButton: ElementRef<MatButton>;
  @Input() settings: SendByEmailSettings;
  @Input() siteId: number;
  @Input() userName: string;
  @ViewChild('chipList') chipList: MatChipList; // ionic chip list does not exists, and this modal is not in mobile so no problem with material

  modalId = 'SendByEmailPopoverComponent';
  private subscriptions = new Subscription();

  separatorKeysCodes: number[] = [ENTER, COMMA, SPACE];
  recipientError: RecipientError = null;
  recipientErrorEnum = RecipientError;
  replyToEmailModeEnum = ReplyToEmailModeEnum;

  replyToEmail: string;
  authorizedDomains = '';
  previewRaw: string; // preview message with all placeholders replaced, except the comment placeholder
  preview: string; // preview message with the comment placeholder replaced in live
  taskId!: string;

  private readonly advisorRealNamePlaceholder = '{{advisor_realname}}';
  private readonly advisorCommentPlaceholder = '{{advisor_comment}}';
  private readonly advisorCommentUserPlaceholder = /\[user\]/g;
  private readonly clientMessagePlaceholder = '{{client_message}}';
  private readonly taskLinkPlaceholder = '{{task_link}}';

  form = this.formBuilder.group({
    recipient: ['', Validators.email], // input for chip list, but data not used for sending email
    recipients: [[], Validators.required], // chip list
    object: ['', Validators.required],
    comment: ['', Validators.required],
  });

  constructor(
    private alcmeonContextService: AlcmeonContextService,
    private basicService: BasicService,
    private formBuilder: FormBuilder,
    private renderer: Renderer2,
    private sendConversationByEmailService: SendConversationByEmailService,
    @Inject(DOCUMENT) public document: Document
  ) {
    super();
  }

  ngOnInit(): void {
    const { email, realname } = this.alcmeonContextService.self;

    switch (this.settings.replyToEmailMode) {
      case ReplyToEmailModeEnum.advisor:
        this.replyToEmail = email;
        break;
      case ReplyToEmailModeEnum.other:
        this.replyToEmail = this.settings.replyToOtherEmail;
        break;
    }

    this.form.get('recipients').setValue(
      this.settings.mandatoryRecipients.map((email: string) => {
        return { email, default: true };
      })
    );

    this.form.get('object').setValue(this.settings.defaultObject);
    this.form.get('comment').setValue(this.settings.defaultComment);

    this.taskId = this.processedTask.id.split(':')[0];
    const taskUrl = [
      environment.frontendURLPrefix,
      '#',
      this.alcmeonContextService.companyId,
      'inbox',
      'task',
      this.taskId,
    ].join('/');

    const finalLoginUrl = [
      environment.frontendURLPrefix,
      '#',
      this.alcmeonContextService.companyCode,
      `login?destUrl=${encodeURIComponent(taskUrl)}`,
    ].join('/');

    this.previewRaw = this.basicService.translate('send-conversation-by-email/default-content');
    this.previewRaw = this.previewRaw.replace(this.advisorRealNamePlaceholder, realname);
    this.previewRaw = this.previewRaw.replace(this.clientMessagePlaceholder, this.processedTask.text);
    this.previewRaw = this.previewRaw.replace(this.taskLinkPlaceholder, finalLoginUrl);
    this.preview = this.previewRaw.replace(this.advisorCommentPlaceholder, this.form.value.comment);
    this.preview = this.preview.replace(this.advisorCommentUserPlaceholder, this.userName);

    this.subscriptions.add(
      this.form.get('comment').valueChanges.subscribe((comment: string) => {
        this.preview = this.previewRaw.replace(this.advisorCommentPlaceholder, comment);
        this.preview = this.preview.replace(this.advisorCommentUserPlaceholder, this.userName);
      })
    );

    this.subscriptions.add(
      this.form.get('recipient').valueChanges.subscribe(() => {
        this.chipList.errorState = false;
      })
    );
  }

  /**
   * Adds a recipient from the input in the chip list
   * Performs all validity controls
   */
  addRecipient(event: MatChipInputEvent) {
    if (!event.value) {
      if (!this.form.value.recipients.length) {
        this.chipList.errorState = true;
        this.recipientError = RecipientError.required;
      }
      return;
    }

    // if valid invalid
    if (this.form.get('recipient').hasError('email')) {
      this.chipList.errorState = true;
      this.recipientError = RecipientError.invalidEmail;
      return;
    }

    // if email already in list
    if (this.form.get('recipients').value.find(elt => elt.email === event.value)) {
      this.chipList.errorState = true;
      this.recipientError = RecipientError.exists;
      event.chipInput.clear();

      // non blocking error, so we remove the message after a delay
      setTimeout(() => {
        this.chipList.errorState = false;
      }, 3000);
      return;
    }

    // if domain not authorized
    if (
      this.settings.authorizedDomains?.length &&
      !this.settings.authorizedDomains.find((domain: string) => event.value.includes(domain))
    ) {
      this.authorizedDomains = this.settings.authorizedDomains.join(' , ');
      this.chipList.errorState = true;
      this.recipientError = RecipientError.domain;
      return;
    }

    // finaly if no error
    this.form.get('recipients').value.push({ email: event.value, default: false });
    this.form.get('recipients').updateValueAndValidity();
    event.chipInput.clear();
  }

  removeRecipient(email: Recipient): void {
    const index = this.form.get('recipients').value.indexOf(email);
    if (index >= 0) {
      this.form.get('recipients').value.splice(index, 1);
      this.form.get('recipients').updateValueAndValidity();
    }
  }

  sendEmail() {
    const addedRecipients = [];
    this.form.get('recipients').value.forEach(recip => {
      if (!recip.default) {
        addedRecipients.push(recip.email);
      }
    });

    const data: SendByEmailPayload = {
      recipients: addedRecipients,
      siteId: this.siteId,
      object: this.form.value.object,
      content: this.preview,
      actionType: ActionType.sendConversation,
      taskId: this.taskId,
    };

    this.sendConversationByEmailService.sendEmail(data).subscribe(() => {
      this.closeModal();
      this.basicService.toastSuccess('outgoing-emails/scheduling-success');
    });
  }

  closeModal() {
    this.basicService.dismissModal();
  }

  minimizeModal() {
    this.document.getElementsByClassName('send-by-email-modal')[0].classList.add('hidden');
    this.renderer.removeClass(this.reOpenButton.nativeElement, 'hidden');
  }

  ngOnDestroy(): void {
    this.subscriptions?.unsubscribe();
  }
}
